import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Search,
  Check,
  X,
  Eye,
  MessageSquare,
  Phone,
  Mail,
  CalendarDays,
  Clock,
  MapPin,
  Download,
  FileDown,
} from "lucide-react";
import { format, parse, subDays, subMonths, subYears, startOfDay, endOfDay } from "date-fns";
import { ar as arLocale } from "date-fns/locale";
import type { DateRange } from "react-day-picker";

import { MerchantShell } from "@/components/merchant/merchant-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Money, Riyal } from "@/components/ui/riyal";



export const Route = createFileRoute("/merchant/bookings")({
  head: () => ({
    meta: [
      { title: "Bookings — Spayzo Merchant" },
      { name: "description", content: "Manage reservations, payments, and customer details." },
    ],
  }),
  component: BookingsPage,
});

type Status = "confirmed" | "completed" | "cancelled" | "refunded";
type Booking = {
  id: string;
  customer: { name: string; email: string; phone: string };
  space: string;
  date: string; // ISO short
  start: string;
  end: string;
  total: number;
  status: Status;
  guests: number;
  addons: { name: string; qty: number; price: number; kind: "rent" | "buy" }[];
  payment: "paid" | "unpaid" | "refunded";
};

const SPACES = ["All spaces", "Studio A", "Meeting Loft", "Photo Bay", "Pod Room", "Rooftop"];

const seed: Booking[] = [
  {
    id: "BK-10428",
    customer: { name: "Sophie Tan", email: "sophie@hey.com", phone: "+1 415 555 0142" },
    space: "Studio A",
    date: "May 23, 2026",
    start: "10:00",
    end: "12:00",
    total: 96,
    status: "confirmed",
    guests: 4,
    payment: "paid",
    addons: [
      { name: "Pro Lighting Kit", qty: 1, price: 18, kind: "rent" },
      { name: "Cold Brew", qty: 4, price: 5, kind: "buy" },
    ],
  },
  {
    id: "BK-10427",
    customer: { name: "Marc Diallo", email: "marc@studio.io", phone: "+1 415 555 0177" },
    space: "Meeting Loft",
    date: "May 23, 2026",
    start: "12:30",
    end: "15:30",
    total: 198,
    status: "confirmed",
    guests: 8,
    payment: "paid",
    addons: [{ name: "4K Webcam", qty: 1, price: 6, kind: "rent" }],
  },
  {
    id: "BK-10426",
    customer: { name: "Yuki Sato", email: "yuki@frame.jp", phone: "+81 90 1234 5678" },
    space: "Photo Bay",
    date: "May 22, 2026",
    start: "15:00",
    end: "18:00",
    total: 282,
    status: "completed",
    guests: 2,
    payment: "paid",
    addons: [{ name: "Tripod — Carbon", qty: 2, price: 8, kind: "rent" }],
  },
  {
    id: "BK-10425",
    customer: { name: "Ines Rivas", email: "ines@flux.co", phone: "+34 600 123 456" },
    space: "Pod Room",
    date: "May 22, 2026",
    start: "17:30",
    end: "18:30",
    total: 22,
    status: "cancelled",
    guests: 1,
    payment: "refunded",
    addons: [],
  },
  {
    id: "BK-10424",
    customer: { name: "Aria Khan", email: "aria@northwave.dev", phone: "+44 7700 900123" },
    space: "Rooftop",
    date: "May 21, 2026",
    start: "18:00",
    end: "22:00",
    total: 540,
    status: "confirmed",
    guests: 22,
    payment: "paid",
    addons: [
      { name: "Cold Brew", qty: 10, price: 5, kind: "buy" },
      { name: "Pro Lighting Kit", qty: 1, price: 18, kind: "rent" },
    ],
  },
];

const statusCfg: Record<Status, { cls: string; dot: string }> = {
  confirmed: { cls: "bg-sky-500/10 text-sky-700 ring-sky-500/20", dot: "bg-sky-500" },
  completed: {
    cls: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20",
    dot: "bg-emerald-500",
  },
  cancelled: { cls: "bg-rose-500/10 text-rose-700 ring-rose-500/20", dot: "bg-rose-500" },
  refunded: { cls: "bg-violet-500/10 text-violet-700 ring-violet-500/20", dot: "bg-violet-500" },
};

const payCfg: Record<Booking["payment"], string> = {
  paid: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20",
  unpaid: "bg-amber-500/10 text-amber-700 ring-amber-500/20",
  refunded: "bg-muted text-muted-foreground ring-border",
};

const B = "pages.merchant.bookings";

function useBookingLabels() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const locale = isAr ? arLocale : undefined;
  return {
    t,
    isAr,
    statusLabel: (s: Status) => t(`${B}.status.${s}`),
    payLabel: (p: Booking["payment"]) => t(`${B}.payment.${p}`),
    spaceLabel: (s: string) => (s === "All spaces" ? t(`${B}.allSpaces`) : t(`${B}.spaceNames.${s}`, s)),
    fmtDate: (value: string) => {
      const parsed = parse(value, "MMMM d, yyyy", new Date());
      return isNaN(parsed.getTime()) ? value : format(parsed, "PP", { locale });
    },
    locale,
  };
}

function BookingsPage() {
  const { t, isAr, statusLabel, spaceLabel, fmtDate, locale } = useBookingLabels();
  const [q, setQ] = useState("");
  const [space, setSpace] = useState("All spaces");
  const [status, setStatus] = useState<"all" | Status>("all");
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [rangePreset, setRangePreset] = useState<"last7" | "month" | "6months" | "year" | "custom" | null>(null);
  const [data, setData] = useState<Booking[]>(seed);
  const [active, setActive] = useState<Booking | null>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());

  const updateStatus = (id: string, next: Status) => {
    setData((rows) =>
      rows.map((r) =>
        r.id === id
          ? { ...r, status: next, payment: next === "cancelled" && r.payment === "paid" ? "refunded" : r.payment }
          : r,
      ),
    );
    setActive((cur) =>
      cur && cur.id === id
        ? { ...cur, status: next, payment: next === "cancelled" && cur.payment === "paid" ? "refunded" : cur.payment }
        : cur,
    );
  };

  const applyPreset = (preset: typeof rangePreset) => {
    const today = new Date();
    let range: DateRange | undefined;
    switch (preset) {
      case "last7":
        range = { from: startOfDay(subDays(today, 7)), to: endOfDay(today) };
        break;
      case "month":
        range = { from: startOfDay(subMonths(today, 1)), to: endOfDay(today) };
        break;
      case "6months":
        range = { from: startOfDay(subMonths(today, 6)), to: endOfDay(today) };
        break;
      case "year":
        range = { from: startOfDay(subYears(today, 1)), to: endOfDay(today) };
        break;
      default:
        range = undefined;
    }
    setDateRange(range);
    setRangePreset(preset);
  };

  const PRESETS = [
    { key: "last7" as const, label: t(`${B}.presets.last7`) },
    { key: "month" as const, label: t(`${B}.presets.month`) },
    { key: "6months" as const, label: t(`${B}.presets.sixMonths`) },
    { key: "year" as const, label: t(`${B}.presets.year`) },
  ];

  const items = useMemo(
    () =>
      data.filter((b) => {
        if (space !== "All spaces" && b.space !== space) return false;
        if (status !== "all" && b.status !== status) return false;
        if (q && ![b.id, b.customer.name, b.space].some((s) => s.toLowerCase().includes(q.toLowerCase()))) return false;
        if (dateRange?.from || dateRange?.to) {
          const bookingDate = parse(b.date, "MMMM d, yyyy", new Date());
          if (dateRange.from && bookingDate < startOfDay(dateRange.from)) return false;
          if (dateRange.to && bookingDate > endOfDay(dateRange.to)) return false;
        }
        return true;
      }),
    [q, space, status, dateRange, data],
  );

  const counts = {
    all: data.length,
    
    confirmed: data.filter((b) => b.status === "confirmed").length,
    completed: data.filter((b) => b.status === "completed").length,
    cancelled: data.filter((b) => b.status === "cancelled").length,
    refunded: data.filter((b) => b.status === "refunded").length,
  };

  const toggle = (id: string) =>
    setSel((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const allChecked = items.length > 0 && items.every((b) => sel.has(b.id));
  const toggleAll = () => setSel(allChecked ? new Set() : new Set(items.map((i) => i.id)));

  const exportPdf = async () => {
    const { jsPDF } = await import("jspdf");
    const rows = status === "all" ? data : data.filter((b) => b.status === status);
    const label = status === "all" ? "All" : status.charAt(0).toUpperCase() + status.slice(1);
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`Bookings — ${label}`, 14, 18);
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(`Generated ${format(new Date(), "MMM d, yyyy p")} · ${rows.length} record(s)`, 14, 25);
    doc.setTextColor(0);

    let y = 36;
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("ID", 14, y);
    doc.text("Customer", 42, y);
    doc.text("Space", 88, y);
    doc.text("Date", 122, y);
    doc.text("Status", 152, y);
    doc.text("Total", 182, y);
    doc.setFont("helvetica", "normal");
    doc.line(14, y + 2, 196, y + 2);
    y += 8;

    rows.forEach((b) => {
      if (y > 280) {
        doc.addPage();
        y = 20;
      }
      doc.text(b.id, 14, y);
      doc.text(b.customer.name.slice(0, 22), 42, y);
      doc.text(b.space.slice(0, 18), 88, y);
      doc.text(`${b.date} ${b.start}`, 122, y);
      doc.text(b.status, 152, y);
      doc.text(`SAR ${b.total}`, 182, y);
      y += 7;
    });

    doc.save(`bookings-${label.toLowerCase()}-${format(new Date(), "yyyy-MM-dd")}.pdf`);
  };

  return (
    <MerchantShell
      title={t("pages.merchant.bookings.title")}
      subtitle={t("pages.merchant.bookings.subtitle")}
      actions={
        <Button variant="outline" className="h-10 rounded-xl" onClick={exportPdf}>
          <Download className="h-4 w-4" /> {t(`${B}.export`)}
        </Button>
      }
    >
      {/* Status chips */}
      <div className="flex flex-wrap gap-2">
        <StatusChip
          label={t(`${B}.status.all`)}
          count={counts.all}
          active={status === "all"}
          onClick={() => setStatus("all")}
        />
        <StatusChip
          label={statusLabel("confirmed")}
          count={counts.confirmed}
          tone="sky"
          active={status === "confirmed"}
          onClick={() => setStatus("confirmed")}
        />
        <StatusChip
          label={statusLabel("completed")}
          count={counts.completed}
          tone="emerald"
          active={status === "completed"}
          onClick={() => setStatus("completed")}
        />
        <StatusChip
          label={statusLabel("cancelled")}
          count={counts.cancelled}
          tone="rose"
          active={status === "cancelled"}
          onClick={() => setStatus("cancelled")}
        />
        <StatusChip
          label={statusLabel("refunded")}
          count={counts.refunded}
          tone="violet"
          active={status === "refunded"}
          onClick={() => setStatus("refunded")}
        />
      </div>


      {/* Filters */}
      <Card className="mt-4 rounded-2xl border-border/60 p-3 shadow-soft sm:p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute start-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t(`${B}.searchPlaceholder`)}
              className="h-11 rounded-xl border-border/70 bg-surface ps-10"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {PRESETS.map((p) => (
              <Button
                key={p.key}
                variant="outline"
                size="sm"
                onClick={() => applyPreset(p.key)}
                className={cn(
                  "h-11 rounded-xl border-border/70 px-4 font-normal",
                  rangePreset === p.key && "border-primary bg-primary text-primary-foreground",
                )}
              >
                {p.label}
              </Button>
            ))}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "h-11 min-w-44 justify-start gap-2 rounded-xl border-border/70 font-normal",
                    rangePreset !== "custom" && !dateRange?.from && "text-muted-foreground",
                    rangePreset === "custom" && "border-primary bg-primary text-primary-foreground",
                  )}
                >
                  <CalendarDays className={cn("h-4 w-4", rangePreset === "custom" ? "text-primary-foreground" : "text-muted-foreground")} />
                  {rangePreset === "custom" && dateRange?.from ? (
                    dateRange.to ? (
                      <span>
                        {format(dateRange.from, "MMM d", { locale })} – {format(dateRange.to, "PP", { locale })}
                      </span>
                    ) : (
                      <span>{format(dateRange.from, "PP", { locale })}</span>
                    )
                  ) : (
                    <span>{t(`${B}.presets.custom`)}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => {
                    setDateRange(range);
                    setRangePreset(range?.from || range?.to ? "custom" : null);
                  }}
                  numberOfMonths={2}
                  initialFocus
                  locale={locale}
                  dir={isAr ? "rtl" : "ltr"}
                  className={cn("p-3 pointer-events-auto")}
                />
                <div className="flex items-center justify-between border-t border-border/60 p-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setDateRange(undefined);
                      setRangePreset(null);
                    }}
                  >
                    {t(`${B}.presets.clear`)}
                  </Button>
                  <span className="pe-2 text-xs text-muted-foreground">
                    {dateRange?.from && dateRange?.to
                      ? `${format(dateRange.from, "PP", { locale })} → ${format(dateRange.to, "PP", { locale })}`
                      : t(`${B}.presets.selectRange`)}
                  </span>
                </div>
              </PopoverContent>
            </Popover>

            <Select value={space} onValueChange={setSpace}>
              <SelectTrigger className="h-11 w-44 rounded-xl">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <SelectValue placeholder={t(`${B}.allSpaces`)}>{spaceLabel(space)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {SPACES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {spaceLabel(s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {sel.size > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-primary/5 px-3 py-2 text-sm">
            <span className="font-semibold">{t(`${B}.bulk.selected`, { count: sel.size })}</span>
            <span className="text-muted-foreground">·</span>
            <Button size="sm" variant="ghost" className="h-8">
              <Check className="h-3.5 w-3.5" /> {t(`${B}.bulk.confirm`)}
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-destructive hover:text-destructive">
              <X className="h-3.5 w-3.5" /> {t(`${B}.bulk.cancel`)}
            </Button>
            <Button size="sm" variant="ghost" className="h-8">
              <MessageSquare className="h-3.5 w-3.5" /> {t(`${B}.bulk.message`)}
            </Button>
            <Button size="sm" variant="ghost" className="ms-auto h-8" onClick={() => setSel(new Set())}>
              {t(`${B}.bulk.clear`)}
            </Button>
          </div>

        )}
      </Card>

      {/* Table */}
      <Card className="mt-5 overflow-hidden rounded-2xl border-border/60 shadow-soft">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="w-10 p-3">
                  <Checkbox aria-label={t(`${B}.selectAll`, "Select all")} checked={allChecked} onCheckedChange={toggleAll} />
                </th>
                <th className="p-3 text-start">{t(`${B}.table.booking`)}</th>
                <th className="p-3 text-start">{t(`${B}.table.customer`)}</th>
                <th className="p-3 text-start">{t(`${B}.table.space`)}</th>
                <th className="p-3 text-start">{t(`${B}.table.when`)}</th>
                <th className="p-3 text-end">{t(`${B}.table.total`)}</th>
                <th className="p-3 text-start">{t(`${B}.table.status`)}</th>
                <th className="w-24 p-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} className="border-t border-border/60 hover:bg-surface/60">
                  <td className="p-3">
                    <Checkbox aria-label={t(`${B}.selectBooking`, "Select booking")} checked={sel.has(b.id)} onCheckedChange={() => toggle(b.id)} />
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => setActive(b)}
                      className="font-mono text-xs font-semibold text-primary hover:underline"
                    >
                      {b.id}
                    </button>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2.5">
                      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
                        {b.customer.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium">{b.customer.name}</p>
                        <p className="truncate text-[11px] text-muted-foreground">{b.customer.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge variant="secondary" className="rounded-full text-[11px]">
                      {spaceLabel(b.space)}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <p className="font-medium">{fmtDate(b.date)}</p>
                    <p className="text-[11px] text-muted-foreground" dir="ltr">
                      {b.start} – {b.end}
                    </p>
                  </td>
                  <td className="p-3 text-end font-semibold"><Riyal /> {b.total.toFixed(2)}</td>
                  <td className="p-3">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
                        statusCfg[b.status].cls,
                      )}
                    >
                      <span className={cn("h-1.5 w-1.5 rounded-full", statusCfg[b.status].dot)} />
                      {statusLabel(b.status)}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => setActive(b)}
                        aria-label={t(`${B}.table.viewDetails`)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-12 text-center">
                    <p className="text-sm font-semibold">{t(`${B}.empty.title`)}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{t(`${B}.empty.desc`)}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <BookingDrawer booking={active} onClose={() => setActive(null)} onStatusChange={updateStatus} />
    </MerchantShell>
  );
}

function StatusChip({
  label,
  count,
  active,
  onClick,
  tone = "neutral",
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  tone?: "neutral" | "amber" | "sky" | "emerald" | "rose" | "violet";
}) {
  const tones: Record<string, string> = {
    neutral: "bg-foreground text-background",
    amber: "bg-amber-500 text-white",
    sky: "bg-sky-500 text-white",
    emerald: "bg-emerald-500 text-white",
    rose: "bg-rose-500 text-white",
    violet: "bg-violet-500 text-white",
  };
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition",
        active
          ? "border-primary bg-primary text-primary-foreground shadow-soft"
          : "border-border bg-background hover:border-primary/50",
      )}
    >
      {label}
      <span
        className={cn(
          "inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px]",
          active ? "bg-primary-foreground/20" : tones[tone],
        )}
      >
        {count}
      </span>
    </button>
  );
}

function BookingDrawer({ booking, onClose, onStatusChange }: { booking: Booking | null; onClose: () => void; onStatusChange: (id: string, next: Status) => void }) {
  const { t, statusLabel, payLabel, spaceLabel, fmtDate } = useBookingLabels();
  const [cancelOpen, setCancelOpen] = useState(false);
  const open = !!booking;
  if (!booking)
    return (
      <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
        <SheetContent />
      </Sheet>
    );
  const b = booking;
  const cfg = statusCfg[b.status];
  const subtotal = b.total - b.addons.reduce((s, a) => s + a.qty * a.price, 0);
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full overflow-y-auto p-0 sm:max-w-lg">
        <SheetHeader className="border-b border-border/60 p-6 text-start">
          <div className="flex items-start justify-between gap-3">
            <div>
              <SheetTitle className="font-display text-xl">{b.customer.name}</SheetTitle>
              <SheetDescription className="font-mono text-xs">{b.id}</SheetDescription>
            </div>
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset",
                cfg.cls,
              )}
            >
              <span className={cn("h-1.5 w-1.5 rounded-full", cfg.dot)} />
              {statusLabel(b.status)}
            </span>
          </div>
        </SheetHeader>

        <div className="space-y-5 p-6">
          {/* Summary */}
          <div className="rounded-2xl bg-surface p-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <MapPin className="h-4 w-4 text-primary" /> {spaceLabel(b.space)}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
              <div className="flex items-center gap-2">
                <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" /> {fmtDate(b.date)}
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                <span dir="ltr">
                  {b.start} → {b.end}
                </span>
              </div>
              <div className="text-muted-foreground">
                {t(`${B}.drawer.guests`)}: <span className="font-semibold text-foreground">{b.guests}</span>
              </div>
              <div className="text-muted-foreground">
                {t(`${B}.drawer.payment`)}:{" "}
                <span
                  className={cn(
                    "font-semibold",
                    b.payment === "paid" && "text-emerald-600",
                    b.payment === "unpaid" && "text-amber-600",
                  )}
                >
                  {payLabel(b.payment)}
                </span>
              </div>
            </div>
          </div>

          {/* Add-ons */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t(`${B}.drawer.selectedProducts`)}
            </p>
            {b.addons.length === 0 ? (
              <p className="rounded-xl border border-dashed p-3 text-xs text-muted-foreground">
                {t(`${B}.drawer.noAddons`)}
              </p>
            ) : (
              <ul className="space-y-2">
                {b.addons.map((a) => (
                  <li
                    key={a.name}
                    className="flex items-center justify-between rounded-xl border border-border/60 p-3 text-sm"
                  >
                    <div>
                      <p className="font-medium">{a.name}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {a.qty} × <Riyal /> {a.price} ·{" "}
                        {a.kind === "rent" ? t(`${B}.drawer.perHour`) : t(`${B}.drawer.perItem`)}
                      </p>
                    </div>
                    <p className="font-semibold"><Riyal /> {(a.qty * a.price).toFixed(2)}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Totals */}
          <div className="rounded-2xl border border-border/60 p-4 text-sm">
            <Row label={t(`${B}.drawer.subtotal`)} value={<Money value={subtotal.toFixed(2)} />} />
            <Row label={t(`${B}.drawer.addons`)} value={<Money value={(b.total - subtotal).toFixed(2)} />} />
            <div className="my-2 border-t border-border/60" />
            <Row label={t(`${B}.drawer.total`)} value={<Money value={b.total.toFixed(2)} />} bold />
            <span
              className={cn(
                "mt-2 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset",
                payCfg[b.payment],
              )}
            >
              {payLabel(b.payment)}
            </span>
          </div>

          {/* Contact */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t(`${B}.drawer.customer`)}
            </p>
            <div className="space-y-2">
              <a
                href={`mailto:${b.customer.email}`}
                className="flex items-center gap-2 rounded-xl border border-border/60 p-3 text-sm hover:bg-surface"
              >
                <Mail className="h-4 w-4 text-muted-foreground" /> <span dir="ltr">{b.customer.email}</span>
              </a>
              <a
                href={`tel:${b.customer.phone}`}
                className="flex items-center gap-2 rounded-xl border border-border/60 p-3 text-sm hover:bg-surface"
              >
                <Phone className="h-4 w-4 text-muted-foreground" /> <span dir="ltr">{b.customer.phone}</span>
              </a>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 flex flex-wrap gap-2 border-t border-border/60 bg-background p-4">
          {b.status === "confirmed" && (
            <Button
              variant="ghost"
              className="h-10 flex-1 rounded-xl text-destructive hover:text-destructive"
              onClick={() => setCancelOpen(true)}
            >
              <X className="h-4 w-4" /> {t(`${B}.drawer.cancel`)}
            </Button>
          )}
          <Button variant="ghost" className="h-10 rounded-xl" onClick={() => downloadInvoice(b)}>
            <FileDown className="h-4 w-4" /> {t(`${B}.drawer.invoice`)}
          </Button>
        </div>
      </SheetContent>
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t(`${B}.cancelDialog.title`, { id: b.id })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(`${B}.cancelDialog.desc`, {
                name: b.customer.name,
                space: spaceLabel(b.space),
                date: fmtDate(b.date),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t(`${B}.cancelDialog.keep`)}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                onStatusChange(b.id, "cancelled");
                setCancelOpen(false);
              }}
            >
              {t(`${B}.cancelDialog.confirm`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}

async function downloadInvoice(b: Booking) {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF();
  const addonsTotal = b.addons.reduce((s, a) => s + a.qty * a.price, 0);
  const subtotal = b.total - addonsTotal;
  let y = 20;
  doc.setFontSize(20);
  doc.text("Spayzo Invoice", 20, y);
  doc.setFontSize(10);
  doc.text(`Booking ID: ${b.id}`, 20, (y += 10));
  doc.text(`Date: ${b.date}   ${b.start} - ${b.end}`, 20, (y += 6));
  doc.text(`Space: ${b.space}`, 20, (y += 6));

  y += 8;
  doc.setFontSize(12);
  doc.text("Bill To", 20, y);
  doc.setFontSize(10);
  doc.text(b.customer.name, 20, (y += 6));
  doc.text(b.customer.email, 20, (y += 5));
  doc.text(b.customer.phone, 20, (y += 5));

  y += 10;
  doc.setFontSize(12);
  doc.text("Items", 20, y);
  doc.setFontSize(10);
  y += 6;
  doc.text("Description", 20, y);
  doc.text("Qty", 130, y);
  doc.text("Price", 150, y);
  doc.text("Total", 175, y);
  doc.line(20, y + 1, 190, y + 1);
  y += 6;
  doc.text(`${b.space} booking`, 20, y);
  doc.text("1", 130, y);
  doc.text(`SAR ${subtotal.toFixed(2)}`, 150, y);
  doc.text(`SAR ${subtotal.toFixed(2)}`, 175, y);
  b.addons.forEach((a) => {
    y += 6;
    doc.text(a.name, 20, y);
    doc.text(String(a.qty), 130, y);
    doc.text(`SAR ${a.price.toFixed(2)}`, 150, y);
    doc.text(`SAR ${(a.qty * a.price).toFixed(2)}`, 175, y);
  });

  y += 10;
  doc.line(20, y, 190, y);
  y += 6;
  doc.setFontSize(12);
  doc.text("Total", 130, y);
  doc.text(`SAR ${b.total.toFixed(2)}`, 175, y);
  doc.setFontSize(10);
  y += 6;
  doc.text(`Payment: ${b.payment}`, 130, y);

  doc.save(`invoice-${b.id}.pdf`);
}

function Row({ label, value, bold }: { label: string; value: React.ReactNode; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className={cn(bold ? "font-display text-base font-bold" : "text-muted-foreground")}>{label}</span>
      <span className={cn(bold ? "font-display text-base font-bold" : "font-semibold")}>{value}</span>
    </div>
  );
}
